import { useNuxtApp } from '#app'
import { safeStrapiTextField } from '~/utils/iterable-live-merge'

/**
 * Optional context for production debugging (placement + route).
 */
export type IterableLogExtras = {
  placementId?: number | string
  /** Original CMS/runtime value when coerced for debugging (e.g. invalid string). */
  placementIdRaw?: unknown
  route?: string
}

function formatExtras (extras?: IterableLogExtras): string {
  if (!extras) return ''
  const parts: string[] = []
  if (extras.placementId != null && extras.placementId !== '') {
    parts.push(`placementId=${extras.placementId}`)
  }
  if (extras.placementIdRaw !== undefined && extras.placementIdRaw !== extras.placementId) {
    const rawPrinted = safeStrapiTextField(extras.placementIdRaw, '')
    parts.push(
      `placementIdRaw=${rawPrinted !== '' ? rawPrinted : typeof extras.placementIdRaw}`
    )
  }
  if (extras.route) {
    parts.push(`route=${extras.route}`)
  }
  return parts.length ? ` [${parts.join(' ')}]` : ''
}

/**
 * Iterable errors: dev console + optional app hook ($iterableError) without breaking Nuxt.
 * The `$iterableError` hook runs in all environments (use for monitoring); console is dev-only.
 * Call only from Nuxt context (plugin, composables, components).
 * Pass `extras` so production logs include placementId + route when debugging.
 */
export function logIterableError (
  context: string,
  err: unknown,
  extras?: IterableLogExtras
): void {
  const suffix = formatExtras(extras)
  if (import.meta.dev) {
    console.warn(`[Iterable] ${context}${suffix}:`, err, extras ?? {})
  }
  try {
    const nuxtApp = useNuxtApp()
    const hook = (nuxtApp as { $iterableError?: (c: string, e: unknown) => void })?.$iterableError
    if (typeof hook === 'function') {
      hook(`${context}${suffix}`, err)
    }
  } catch {
    /* useNuxtApp unavailable outside Nuxt lifecycle */
  }
}
